import { cva, type VariantProps } from 'class-variance-authority'
import { cn } from 'designSystem/lib/utils'
import { ArrowDown, ArrowUp, ArrowUpDown } from 'lucide-react'
import * as React from 'react'

const tableLayouts = cva('caption-bottom text-sm', {
  variants: {
    layout: {
      default: 'w-full',
      prose: 'w-full table-auto border-collapse',
      fixed: 'w-full table-fixed',
    },
  },
  defaultVariants: {
    layout: 'default',
  },
})

const tableRowLayouts = cva('', {
  variants: {
    layout: {
      default:
        'border-b transition-colors hover:bg-muted/50 data-[state=selected]:bg-muted',
      prose: '',
    },
  },
  defaultVariants: {
    layout: 'default',
  },
})

const tableHeadSizes = cva(
  'font-medium [&:has([role=checkbox])]:pr-0 [&>[role=checkbox]]:translate-y-[2px]',
  {
    variants: {
      align: {
        left: 'text-left',
        center: 'text-center',
        right: 'text-right',
      },
      size: {
        default: 'h-10 px-2',
        compact: 'h-8 px-2 text-xs',
      },
      layout: {
        default: 'align-middle whitespace-nowrap text-muted-foreground',
        prose:
          'h-auto align-top whitespace-normal max-w-prose-cell break-words border border-border bg-muted py-1.5 font-semibold text-foreground',
        clip: 'align-middle whitespace-nowrap text-muted-foreground min-w-0 overflow-hidden',
        stack:
          'h-auto align-top whitespace-normal text-muted-foreground min-w-0 overflow-hidden',
      },
    },
    defaultVariants: {
      size: 'default',
      align: 'left',
      layout: 'default',
    },
  }
)

const tableCellSizes = cva(
  '[&:has([role=checkbox])]:pr-0 [&>[role=checkbox]]:translate-y-[2px]',
  {
    variants: {
      size: {
        default: 'p-2',
        compact: 'px-2 py-1.5 text-xs',
        none: 'p-0',
      },
      layout: {
        default: 'align-middle whitespace-nowrap',
        wrap: 'align-middle whitespace-pre-wrap [overflow-wrap:anywhere] min-w-40 max-w-prose-cell',
        prose:
          'align-top whitespace-normal max-w-prose-cell break-words border border-border',
        clip: 'align-middle whitespace-nowrap min-w-0 overflow-hidden',
        truncate: 'align-middle min-w-0 truncate',
      },
    },
    defaultVariants: {
      size: 'default',
      layout: 'default',
    },
  }
)

type TableLayout = NonNullable<VariantProps<typeof tableLayouts>['layout']>

function Table({
  className,
  scroll = 'auto',
  layout = 'default',
  ...props
}: React.ComponentProps<'table'> & {
  scroll?: 'auto' | 'none'
  layout?: TableLayout
}) {
  return (
    <div
      className={cn(
        'relative w-full',
        layout === 'prose' && 'my-2 max-w-full',
        scroll === 'auto' && 'overflow-x-auto'
      )}>
      <table className={cn(tableLayouts({ layout }), className)} {...props} />
    </div>
  )
}

function TableHeader({
  className,
  layout = 'default',
  sticky = false,
  ...props
}: React.ComponentProps<'thead'> & {
  layout?: TableLayout
  sticky?: boolean
}) {
  return (
    <thead
      className={cn(
        layout === 'default' && '[&_tr]:border-b',
        sticky &&
          '[&_th]:sticky [&_th]:top-0 [&_th]:z-10 [&_th]:bg-card [&_tr]:hover:bg-transparent [&_th]:shadow-[inset_0_-1px_0_var(--border)]',
        className
      )}
      {...props}
    />
  )
}

function TableBody({ className, ...props }: React.ComponentProps<'tbody'>) {
  return (
    <tbody className={cn('[&_tr:last-child]:border-0', className)} {...props} />
  )
}

function TableFooter({ className, ...props }: React.ComponentProps<'tfoot'>) {
  return (
    <tfoot
      className={cn(
        'border-t bg-muted/50 font-medium [&>tr]:last:border-b-0',
        className
      )}
      {...props}
    />
  )
}

function TableRow({
  className,
  layout = 'default',
  ...props
}: React.ComponentProps<'tr'> & VariantProps<typeof tableRowLayouts>) {
  return (
    <tr className={cn(tableRowLayouts({ layout }), className)} {...props} />
  )
}

function TableHead({
  className,
  align = 'left',
  size = 'default',
  layout = 'default',
  ...props
}: React.ComponentProps<'th'> & VariantProps<typeof tableHeadSizes>) {
  return (
    <th
      className={cn(tableHeadSizes({ size, layout, align }), className)}
      {...props}
    />
  )
}

function TableCell({
  className,
  size = 'default',
  layout = 'default',
  ...props
}: React.ComponentProps<'td'> & VariantProps<typeof tableCellSizes>) {
  return (
    <td
      className={cn(tableCellSizes({ size, layout }), className)}
      {...props}
    />
  )
}

function TableSortButton({
  className,
  direction = 'none',
  children,
  ...props
}: React.ComponentProps<'button'> & {
  direction?: 'none' | 'ascending' | 'descending'
}) {
  const Icon =
    direction === 'ascending'
      ? ArrowUp
      : direction === 'descending'
        ? ArrowDown
        : ArrowUpDown
  return (
    <button
      type='button'
      data-slot='table-sort-button'
      className={cn(
        'group/sort inline-flex cursor-pointer items-center gap-1 rounded-sm text-inherit font-medium whitespace-nowrap outline-none hover:text-foreground focus-visible:text-foreground focus-visible:ring-2 focus-visible:ring-ring/30 disabled:pointer-events-none disabled:opacity-50',
        className
      )}
      {...props}>
      {children}
      <Icon
        aria-hidden
        className={cn(
          'size-3.5 shrink-0',
          direction === 'none' &&
            'opacity-0 group-hover/sort:opacity-100 group-focus-visible/sort:opacity-100'
        )}
      />
    </button>
  )
}

function TableCaption({
  className,
  ...props
}: React.ComponentProps<'caption'>) {
  return (
    <caption
      className={cn('mt-4 text-sm text-muted-foreground', className)}
      {...props}
    />
  )
}

export {
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
  TableSortButton,
}
